import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ChartNoAxesCombined,
  ChevronRight,
  Home,
  ListOrdered,
  LayoutGrid,
  Menu,
  Users,
  X,
} from "lucide-react";
import { useAuthStore } from "@/store/useAuthStore";
import { isTeacher } from "@/utils/role-utils";
import { useAccounts, accountName } from "@/hooks/useAccounts";
import { useClassrooms } from "@/hooks/useClassrooms";
import { useSequencingReachable } from "@/hooks/useSequencingReachable";
import ProfileDropdown from "../ProfileDropdown";
import AccountSwitcher from "../AccountSwitcher";
import "./workspace.css";
import {
  activeNavSection,
  buildBreadcrumbs,
  classroomContextId,
  type NavSectionKey,
  dashboardTabHref,
  resolveDashboardTab,
  teacherGroupId,
  visibleDashboardTabs,
  withClassroomContext,
} from "./navigation";

export function WorkspaceBrand() {
  return (
    <div className="et-brand">
      <span
        className="et-brand-wordmark"
        role="img"
        aria-label="Evertutor Logo"
      />
    </div>
  );
}

export default function WorkspaceShell({ children }: { children: ReactNode }) {
  const { user, actingTeacherId } = useAuthStore();
  const teacher = isTeacher(user?.role);
  const { accounts } = useAccounts();
  const location = useLocation();
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);
  const navigationToggleRef = useRef<HTMLButtonElement>(null);
  const navigationCloseRef = useRef<HTMLButtonElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);
  const skipLinkRef = useRef<HTMLAnchorElement>(null);
  const workspaceBodyRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  /** Where focus goes once the drawer has closed; null means "decide then". */
  const focusAfterCloseRef = useRef<HTMLElement | null>(null);
  useEffect(
    () => setMobileNavigationOpen(false),
    [location.pathname, location.search],
  );
  const closeMobileNavigation = () => {
    focusAfterCloseRef.current = navigationToggleRef.current;
    setMobileNavigationOpen(false);
  };
  // While open, the drawer is modal: the page behind it is inert, so neither
  // Tab nor a screen reader can reach what the backdrop covers. It only
  // exists below 768px; leaving that width must not strand it open.
  useEffect(() => {
    if (!mobileNavigationOpen) return;
    const sidebar = sidebarRef.current;
    const main = mainRef.current;
    const background = [skipLinkRef.current, workspaceBodyRef.current];
    background.forEach((element) => element?.setAttribute("inert", ""));
    navigationCloseRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      focusAfterCloseRef.current = navigationToggleRef.current;
      setMobileNavigationOpen(false);
    };
    const desktopQuery = window.matchMedia?.("(min-width: 768px)");
    const handleDesktopChange = () => {
      if (desktopQuery?.matches) setMobileNavigationOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    desktopQuery?.addEventListener("change", handleDesktopChange);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      desktopQuery?.removeEventListener("change", handleDesktopChange);
      background.forEach((element) => element?.removeAttribute("inert"));
      // Focus moves only now: an inert toggle cannot take it. Closed by a
      // navigation, the drawer would hide the link that still holds focus,
      // so the new page's content takes it instead.
      const isFocusInDrawer = Boolean(
        sidebar?.contains(document.activeElement),
      );
      const target =
        focusAfterCloseRef.current ??
        (isFocusInDrawer && !desktopQuery?.matches ? main : null);
      focusAfterCloseRef.current = null;
      target?.focus();
    };
  }, [mobileNavigationOpen]);
  const classroomId = teacher
    ? undefined
    : classroomContextId(location.pathname, location.search);
  const target = accounts.find((a) => a.teacher_id === actingTeacherId);
  const ownerName = target
    ? accountName(target)
    : user?.name || user?.username || "";
  const workspaceLabel = ownerName
    ? `${ownerName}’s workspace`
    : "Your workspace";
  const dashboard = location.pathname === "/analytics";
  const dashboardTabs = visibleDashboardTabs(user);
  const selectedTab = resolveDashboardTab(
    new URLSearchParams(location.search).get("tab"),
    user,
  );
  const sequencingReachable = useSequencingReachable(teacher);
  const spacesLink = {
    key: "spaces" as const,
    to: withClassroomContext("/spaces", classroomId),
    label: "Spaces",
    icon: LayoutGrid,
  };
  // A fixed set per role: items never appear or vanish as the route changes.
  // A student's classroom is reached from Home and named in the breadcrumb.
  const links = teacher
    ? [
        { key: "home" as const, to: "/lessons", label: "Home", icon: Home },
        spacesLink,
        ...(sequencingReachable
          ? [
              {
                key: "sequencing" as const,
                to: "/lesson-sequencing",
                label: "Sequencing",
                icon: ListOrdered,
              },
            ]
          : []),
        {
          key: "groups" as const,
          to: "/classrooms",
          label: "Groups",
          icon: Users,
        },
        {
          key: "dashboard" as const,
          to: "/analytics",
          label: "Dashboard",
          icon: ChartNoAxesCombined,
        },
      ]
    : [
        { key: "home" as const, to: "/classrooms", label: "Home", icon: Home },
        spacesLink,
      ];
  const activeSection = activeNavSection(location.pathname, teacher);
  const groupId = teacher
    ? teacherGroupId(location.pathname, location.search)
    : undefined;
  // Names only; a cached list is reused, so this fetches at most once per
  // viewer, and joins the request a classrooms page makes alongside it.
  const { classrooms } = useClassrooms({
    isEnabled: Boolean(classroomId || groupId),
  });
  const contextId = classroomId ?? groupId;
  const contextClassroom = classrooms?.find((c) => String(c.id) === contextId);
  // Until the list arrives the generic label stands in for the name.
  const classroomLabel = contextClassroom?.name ?? "Your classroom";
  // A group id the loaded list does not hold names no group of this teacher.
  const isGroupKnown =
    groupId !== undefined && (classrooms === null || Boolean(contextClassroom));
  // Sub-items under a section: the dashboard tabs while on the dashboard, a
  // teacher's open group under Groups, and a student's open classroom under
  // Home (also while in its Spaces).
  const subnavs: Partial<
    Record<
      NavSectionKey,
      {
        label: string;
        items: { key: string; to: string; label: string; isCurrent: boolean }[];
      }
    >
  > = {
    ...(dashboard && {
      dashboard: {
        label: "Dashboard navigation",
        items: dashboardTabs.map((tab) => ({
          key: tab.key,
          to: dashboardTabHref(location.search, tab.key),
          label: tab.label,
          isCurrent: selectedTab === tab.key,
        })),
      },
    }),
    ...(groupId &&
      isGroupKnown && {
        groups: {
          label: "Group navigation",
          items: [
            {
              key: groupId,
              to: `/classrooms?group=${groupId}`,
              label: contextClassroom?.name ?? "Selected group",
              isCurrent: true,
            },
          ],
        },
      }),
    ...(classroomId && {
      home: {
        label: "Classroom navigation",
        items: [
          {
            key: classroomId,
            to: `/classroom/${classroomId}`,
            label: classroomLabel,
            isCurrent: location.pathname === `/classroom/${classroomId}`,
          },
        ],
      },
    }),
  };
  const breadcrumbs = buildBreadcrumbs({
    pathname: location.pathname,
    search: location.search,
    isTeacherMenu: teacher,
    contextName: teacher
      ? groupId && isGroupKnown
        ? (contextClassroom?.name ?? "Selected group")
        : null
      : (contextClassroom?.name ?? null),
    dashboardTabLabel:
      dashboardTabs.find((t) => t.key === selectedTab)?.label || "Analytics",
  });
  return (
    <div className={`et-workspace ${teacher ? "et-teacher" : "et-student"}`}>
      <a ref={skipLinkRef} className="et-skip" href="#workspace-main">
        Skip to main content
      </a>
      {mobileNavigationOpen && (
        <div
          className="et-sidebar-backdrop"
          aria-hidden="true"
          onClick={closeMobileNavigation}
        />
      )}
      <aside
        ref={sidebarRef}
        id="workspace-navigation"
        className={`et-sidebar${mobileNavigationOpen ? " is-open" : ""}`}
      >
        <button
          type="button"
          ref={navigationCloseRef}
          className="et-sidebar-close"
          aria-label="Close navigation"
          onClick={closeMobileNavigation}
        >
          <X size={22} />
        </button>
        <Link
          className="et-brand-link"
          to={teacher ? "/lessons" : "/classrooms"}
          aria-label="Evertutor Live home"
        >
          <WorkspaceBrand />
        </Link>
        <div className="et-workspace-identity">
          <span className="et-monogram" aria-hidden="true">
            {workspaceLabel.slice(0, 1)}
          </span>
          <span>
            {workspaceLabel}
            <small>
              {teacher
                ? actingTeacherId
                  ? "TEACHER"
                  : user?.role?.replace(/_/g, " ")
                : "STUDENT"}
            </small>
          </span>
        </div>
        <nav className="et-navigation" aria-label="Main navigation">
          {links.map(({ key, to, label, icon: Icon }) => {
            const isActive = key === activeSection;
            const subnav = subnavs[key];
            // A current sub-item carries the highlight; its parent stays
            // marked but plain, so one row stands for the current page.
            const hasCurrentChild =
              isActive && Boolean(subnav?.items.some((item) => item.isCurrent));
            const isExactPage = location.pathname === to.split("?")[0];
            return (
              <div key={key}>
                <Link
                  to={to}
                  aria-label={label}
                  title={label}
                  aria-current={
                    isActive
                      ? isExactPage && !hasCurrentChild
                        ? "page"
                        : "true"
                      : undefined
                  }
                  className={`et-nav-link${isActive ? " active" : ""}${hasCurrentChild ? " has-current-child" : ""}`}
                >
                  <Icon size={19} strokeWidth={1.7} />
                  <span>{label}</span>
                </Link>
                {subnav && (
                  <nav className="et-subnav" aria-label={subnav.label}>
                    {subnav.items.map((item) => (
                      <Link
                        key={item.key}
                        to={item.to}
                        aria-current={item.isCurrent ? "page" : undefined}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </nav>
                )}
              </div>
            );
          })}
        </nav>
        <div className="et-sidebar-footer">
          {teacher && <AccountSwitcher />}
          {/* Settings open in a portalled sheet that sits below the drawer,
              so the drawer steps aside first; the sheet's state lives on. */}
          <ProfileDropdown onOpen={() => setMobileNavigationOpen(false)} />
        </div>
      </aside>
      <div ref={workspaceBodyRef} className="et-workspace-body">
        <header className="et-topbar">
          <button
            type="button"
            ref={navigationToggleRef}
            className="et-mobile-navigation-toggle"
            aria-label="Toggle navigation"
            aria-controls="workspace-navigation"
            aria-expanded={mobileNavigationOpen}
            onClick={() => setMobileNavigationOpen(!mobileNavigationOpen)}
          >
            <Menu size={22} />
          </button>
          <nav
            className="et-breadcrumbs"
            aria-label="Breadcrumb"
            hidden={breadcrumbs.length === 0}
          >
            <ol>
              {breadcrumbs.map((crumb, index) => {
                const isPage = index === breadcrumbs.length - 1;
                return (
                  <li key={`${index}:${crumb.label}`}>
                    {index > 0 && <ChevronRight size={14} aria-hidden="true" />}
                    {isPage ? (
                      <span aria-current="page">{crumb.label}</span>
                    ) : crumb.to ? (
                      <Link to={crumb.to}>{crumb.label}</Link>
                    ) : (
                      <span>{crumb.label}</span>
                    )}
                  </li>
                );
              })}
            </ol>
          </nav>
        </header>
        <main
          ref={mainRef}
          className="et-main"
          id="workspace-main"
          tabIndex={-1}
        >
          {children}
        </main>
      </div>
    </div>
  );
}
